import React, { useEffect, useRef } from "react";
import { Link } from "gatsby";
import Layout from "components/layout";
import Image from "components/image";
import Style from "styles/index.module.scss";
import SEO from "components/seo";
import LinkButton from "components/link_button";
import BackgroundVideo from "components/background_video";
import YoutubeEmbed from "components/youtube_embed";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import FvPoster from "images/fv_background.png";

gsap.registerPlugin(ScrollTrigger);

const Index = () => {
  const videoSrc = {
    url: "/e_ize_gradation.mp4",
    poster: FvPoster,
  };
  const topCatchEnRef = useRef(null);
  const topCatchJaRef = useRef(null);
  const bannerWrapperRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    const enEl = topCatchEnRef.current;
    const jaEl = topCatchJaRef.current;
    if (!enEl) return;

    const words = enEl.querySelectorAll(`.${Style.topCatch__word}`);
    const tl = gsap.timeline({ delay: 0.3 });

    gsap.set(words, { opacity: 0, y: 80 });
    tl.to(words, {
      opacity: 1,
      y: 0,
      duration: 1.0,
      stagger: 0.25,
      ease: "expo.out",
    });

    if (jaEl) {
      const chars = jaEl.querySelectorAll(`.${Style.topCatch__char}`);
      gsap.set(chars, { opacity: 0 });
      tl.to(chars, {
        opacity: 1,
        duration: 0.2,
        stagger: 0.018,
        ease: "power2.out",
      }, "-=0.4");
    }

    const bannerEl = bannerWrapperRef.current;
    if (bannerEl) {
      const banners = Array.from(bannerEl.children);
      gsap.set(banners, { x: 300, opacity: 0 });
      tl.to(banners[0], {
        x: 0,
        opacity: 1,
        duration: 0.5,
        ease: "power3.out",
      });
      if (banners[1]) {
        tl.to(banners[1], {
          x: 0,
          opacity: 1,
          duration: 0.5,
          ease: "power3.out",
        }, "-=0.25");
      }
    }

    // ScrollTrigger アニメーション
    const root = contentRef.current;
    if (!root) return;

    // セクション見出し: reveal-up
    root.querySelectorAll(`.${Style.mask}`).forEach((mask) => {
      const inner = mask.querySelector(`.${Style.revealUp}`);
      if (!inner) return;
      gsap.set(inner, { y: "105%" });
      gsap.to(inner, {
        y: 0,
        duration: 1.2,
        ease: "power4.out",
        scrollTrigger: { trigger: mask, start: "top 80%" },
      });
    });

    // セクション画像: fade + slide-up
    root.querySelectorAll(`.${Style.fadeUp}`).forEach((el) => {
      gsap.set(el, { opacity: 0, y: 60 });
      gsap.to(el, {
        opacity: 1,
        y: 0,
        duration: 1.0,
        ease: "power3.out",
        scrollTrigger: { trigger: el, start: "top 80%" },
      });
    });

    // サービスアイテム: fade + slide-up (stagger)
    const items = root.querySelectorAll(`.${Style.serviceItem}`);
    if (items.length) {
      gsap.set(items, { opacity: 0, y: 80 });
      ScrollTrigger.batch(items, {
        start: "top 85%",
        onEnter: (batch) => {
          gsap.to(batch, {
            opacity: 1,
            y: 0,
            duration: 1.0,
            stagger: 0.2,
            ease: "power3.out",
          });
        },
        once: true,
      });
    }

    // serviceIntroテキスト: reveal
    const introText = root.querySelector(`.${Style.serviceIntro__text}`);
    if (introText) {
      const introEls = [
        introText.querySelector(`.${Style.serviceIntro__heading}`),
        introText.querySelector(`.${Style.serviceIntro__body}`),
      ].filter(Boolean);
      gsap.set(introEls, { opacity: 0, y: 40 });
      gsap.to(introEls, {
        opacity: 1,
        y: 0,
        duration: 1.2,
        stagger: 0.15,
        ease: "power4.out",
        scrollTrigger: { trigger: introText, start: "top 70%" },
      });
    }

    // ADVANTAGE コンテンツ: fade + slide-up
    const advContent = root.querySelector(`.${Style.advantageContent}`);
    if (advContent) {
      gsap.set(advContent, { opacity: 0, y: 40 });
      gsap.to(advContent, {
        opacity: 1,
        y: 0,
        duration: 1.0,
        ease: "power3.out",
        scrollTrigger: { trigger: advContent, start: "top 80%" },
      });
    }

    // RECRUIT 2カラム: 左右からスライドイン
    const txtCol = root.querySelector(`.${Style.recruitContent__txtCol}`);
    const videoCol = root.querySelector(`.${Style.recruitContent__videoCol}`);
    if (txtCol) {
      gsap.set(txtCol, { opacity: 0, x: -40 });
      gsap.to(txtCol, {
        opacity: 1,
        x: 0,
        duration: 1.0,
        ease: "power3.out",
        scrollTrigger: { trigger: txtCol, start: "top 80%" },
      });
    }
    if (videoCol) {
      gsap.set(videoCol, { opacity: 0, x: 40 });
      gsap.to(videoCol, {
        opacity: 1,
        x: 0,
        duration: 1.0,
        delay: 0.2,
        ease: "power3.out",
        scrollTrigger: { trigger: videoCol, start: "top 80%" },
      });
    }

    return () => ScrollTrigger.getAll().forEach((t) => t.kill());
  }, []);

  return (
    <>
      <SEO title="e-ize" />
      <Layout>
        <div className={Style.container}>
          <div className={Style.Content} ref={contentRef}>
            <section className={Style.top}>
              <div className={Style.top__inner}>
                <BackgroundVideo videoSrc={videoSrc} />
                <div className={Style.topCatch}>
                  <div className={Style.topCatch__en} ref={topCatchEnRef}>
                    <span className={Style.topCatch__word}>CREATE</span>
                    <br />
                    <span className={Style.topCatch__word}>THE</span>{" "}
                    <span className={Style.topCatch__word}>FUTURE</span>
                  </div>
                  <div className={Style.topCatch__ja} ref={topCatchJaRef}>
                    {"事業に寄り添う、".split("").map((char, i) => (
                      <span key={`l1a-${i}`} className={Style.topCatch__char}>{char}</span>
                    ))}
                    <span className={Style.topCatch__gradient}>
                      {"実践的なテクノロジー".split("").map((char, i) => (
                        <span key={`l1b-${i}`} className={Style.topCatch__char}>{char}</span>
                      ))}
                    </span>
                    <br />
                    {"立ち上げ期から拡大期まで".split("").map((char, i) => (
                      <span key={`l2-${i}`} className={Style.topCatch__char}>{char}</span>
                    ))}
                    <br />
                    <span className={Style.topCatch__gradient}>
                      {"技術支援で事業成長を後押しします".split("").map((char, i) => (
                        <span key={`l3-${i}`} className={Style.topCatch__char}>{char}</span>
                      ))}
                    </span>
                  </div>
                </div>
                <div className={Style.top__img__award__banner__wrapper} ref={bannerWrapperRef}>
                  <a href="https://www.your-intern.com/award/2021/" target="_blank" rel="noopener noreferrer">
                    <Image className={Style.top__img__award__banner} filename="award2021.png" alt="インターンアワード2021" />
                  </a>
                  <a
                    href="https://www.telework-rule.metro.tokyo.lg.jp/search/details.php?app_form_id=647578"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Image className={Style.top__img__award__banner_margin} filename="logo3.png" alt="テレワーク東京ルール実践企業" />
                  </a>
                </div>
              </div>
            </section>
            <section className={Style.service}>
              <div className={Style.service__inner}>
                <div className={Style.mask}>
                  <h2 className={`${Style.service__head} ${Style.revealUp}`}>
                    SERVICE
                  </h2>
                </div>
                <div className={Style.serviceIntro}>
                  <div className={Style.serviceIntro__slide}>
                    <div className={Style.serviceIntro__col}>
                      <div className={`${Style.serviceIntro__track} ${Style.serviceIntro__trackUp}`}>
                        <Image className={Style.serviceIntro__img} filename="e_ize_member_40.png" alt="" />
                        <Image className={Style.serviceIntro__img} filename="e_ize_member_49.png" alt="" />
                        <Image className={Style.serviceIntro__img} filename="e_ize_member_16.png" alt="" />
                        <Image className={Style.serviceIntro__img} filename="e_ize_member_46.png" alt="" />
                        <Image className={Style.serviceIntro__img} filename="e_ize_member_8.png" alt="" />
                        <Image className={Style.serviceIntro__img} filename="e_ize_member_51.png" alt="" />
                        <Image className={Style.serviceIntro__img} filename="e_ize_member_12.png" alt="" />
                        <Image className={Style.serviceIntro__img} filename="e_ize_member_26.png" alt="" />
                        <Image className={Style.serviceIntro__img} filename="e_ize_member_40.png" alt="" />
                        <Image className={Style.serviceIntro__img} filename="e_ize_member_49.png" alt="" />
                        <Image className={Style.serviceIntro__img} filename="e_ize_member_16.png" alt="" />
                        <Image className={Style.serviceIntro__img} filename="e_ize_member_46.png" alt="" />
                        <Image className={Style.serviceIntro__img} filename="e_ize_member_8.png" alt="" />
                        <Image className={Style.serviceIntro__img} filename="e_ize_member_51.png" alt="" />
                        <Image className={Style.serviceIntro__img} filename="e_ize_member_12.png" alt="" />
                        <Image className={Style.serviceIntro__img} filename="e_ize_member_26.png" alt="" />
                      </div>
                    </div>
                    <div className={Style.serviceIntro__col}>
                      <div className={`${Style.serviceIntro__track} ${Style.serviceIntro__trackDown}`}>
                        <Image className={Style.serviceIntro__img} filename="e_ize_member_47.png" alt="" />
                        <Image className={Style.serviceIntro__img} filename="e_ize_member_48.png" alt="" />
                        <Image className={Style.serviceIntro__img} filename="e_ize_member_52.png" alt="" />
                        <Image className={Style.serviceIntro__img} filename="e_ize_member_38.png" alt="" />
                        <Image className={Style.serviceIntro__img} filename="e_ize_member_7.png" alt="" />
                        <Image className={Style.serviceIntro__img} filename="e_ize_member_11.png" alt="" />
                        <Image className={Style.serviceIntro__img} filename="e_ize_member_22.png" alt="" />
                        <Image className={Style.serviceIntro__img} filename="e_ize_member.png" alt="" />
                        <Image className={Style.serviceIntro__img} filename="e_ize_member_47.png" alt="" />
                        <Image className={Style.serviceIntro__img} filename="e_ize_member_48.png" alt="" />
                        <Image className={Style.serviceIntro__img} filename="e_ize_member_52.png" alt="" />
                        <Image className={Style.serviceIntro__img} filename="e_ize_member_38.png" alt="" />
                        <Image className={Style.serviceIntro__img} filename="e_ize_member_7.png" alt="" />
                        <Image className={Style.serviceIntro__img} filename="e_ize_member_11.png" alt="" />
                        <Image className={Style.serviceIntro__img} filename="e_ize_member_22.png" alt="" />
                        <Image className={Style.serviceIntro__img} filename="e_ize_member.png" alt="" />
                      </div>
                    </div>
                  </div>
                  <div className={Style.serviceIntro__text}>
                    <p className={Style.serviceIntro__heading}>
                      加速する<span className={Style.serviceIntro__highlight}>DX</span>時代に、<br />
                      チームの総力で<span className={Style.serviceIntro__highlight}>最善の一手</span>を。
                    </p>
                    <p className={Style.serviceIntro__body}>
                      ビジネスモデルや組織の変革のために企業のDXが加速している現代。
                      私たちは市場や関係者様の要望に対し、組織一丸となって最適なソリューションをご提供します。
                    </p>
                  </div>
                </div>
                <div
                  className={Style.serviceItem}
                >
                  <a className={Style.serviceItem__link} href="https://www.comachicart.jp/" target="_blank" rel="noopener noreferrer">
                    <div className={Style.serviceItem__txtWrapper}>
                      <h3 className={Style.serviceItem__head}>comachicart</h3>
                      <p className={Style.serviceItem__txt}>ショッピングカートサービスをご提供</p>
                    </div>
                    <div className={Style.serviceItem__imgWrapper}>
                      <Image
                        className={Style.serviceItem__img}
                        filename="service__comachi_3.png"
                        alt="comachicart"
                      />
                    </div>
                  </a>
                </div>
                <div
                  className={Style.serviceItem}
                >
                  <Link className={Style.serviceItem__link} to="/service/#ses">
                    <div className={Style.serviceItem__txtWrapper}>
                      <h3 className={Style.serviceItem__head}>SES</h3>
                      <p className={Style.serviceItem__txt}>優秀な人材をご提供</p>
                    </div>
                    <div className={Style.serviceItem__imgWrapper}>
                      <Image className={Style.serviceItem__img} filename="service_ses_3.png" alt="SES" />
                    </div>
                  </Link>
                </div>
                <div
                  className={Style.serviceItem}
                >
                  <Link className={Style.serviceItem__link} to="/service/#development">
                    <div className={Style.serviceItem__txtWrapper}>
                      <h3 className={Style.serviceItem__head}>Development</h3>
                      <p className={Style.serviceItem__txt}>最大限のソリューションをご提供</p>
                    </div>
                    <div className={Style.serviceItem__imgWrapper}>
                      <Image className={Style.serviceItem__img} filename="service_dev_3.png" alt="Development" />
                    </div>
                  </Link>
                </div>
                <div className={Style.service__buttonBox}>
                  <LinkButton className={Style.service__button} color="black" text="MORE" url="/service/" />
                </div>
              </div>
            </section>
            <section className={Style.advantage}>
              <div className={Style.advantage__inner}>
                <div className={Style.mask}>
                  <h2 className={`${Style.advantage__head} ${Style.revealUp}`}>
                    ADVANTAGE
                  </h2>
                </div>
                <div className={`${Style.advantage__img} ${Style.fadeUp}`}>
                  <Image filename="e_ize_member_11.png" alt="イーゼの強み" />
                </div>

                <div className={Style.advantageContent}>
                  <div className={Style.advantageContent__inner}>
                    <p className={Style.advantageContent__txt}>
                      社員のアイデアや要望を形にした自社システム。
                      エンジニアとしてのスキルアップを目的としたワークショップ。
                      各々のキャリアパス形成をサポートする目標管理制度。
                      イーゼには新しい技術を吸収し、形にできる環境があります。
                    </p>
                    <div className={Style.advantageContent__buttonBox}>
                      <LinkButton
                        className={Style.advantageContent__button}
                        color="black"
                        text="ABOUT US"
                        url="/about/"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className={Style.recruit}>
              <div className={Style.recruit__inner}>
                <div className={Style.mask}>
                  <h2 className={`${Style.recruit__head} ${Style.revealUp}`}>
                    RECRUIT
                  </h2>
                </div>
                <div className={`${Style.recruit__img} ${Style.fadeUp}`}>
                  <Image filename="recruit.png" alt="採用情報" />
                </div>

                <div className={`${Style.recruitInterview} ${Style.fadeUp}`}>
                  <div className={Style.recruitInterview__head}>
                    <h2 className={Style.recruitInterview__title}>INTERVIEW</h2>
                    <p className={Style.recruitInterview__sub}>社員インタビュー</p>
                  </div>
                  <div className={Style.recruitInterview__inner}>
                    <div className={Style.recruitInterview__videoCol}>
                      <YoutubeEmbed videoId="l_CKHWYgzRw" title="INTERVIEW 01" />
                      <p className={Style.recruitInterview__caption}>INTERVIEW 01</p>
                    </div>
                    <div className={Style.recruitInterview__videoCol}>
                      <YoutubeEmbed videoId="DvcmMeIUYyE" title="INTERVIEW 02" />
                      <p className={Style.recruitInterview__caption}>INTERVIEW 02</p>
                    </div>
                  </div>
                </div>

                <div className={Style.recruitContent}>
                  <div className={`${Style.recruitContent__head} ${Style.fadeUp}`}>
                    <h2 className={Style.recruitContent__title}>CULTURE</h2>
                    <p className={Style.recruitContent__sub}>e-izeの社風</p>
                  </div>
                  <div className={Style.recruitContent__inner}>
                    <div className={Style.recruitContent__txtCol}>
                      <p className={Style.recruitContent__txt}>
                        イーゼは、エンジニアの主体性を最大限に尊重し一人ひとりのキャリアパスに真剣に向き合います。
                        私たちと一緒に挑戦してくれる仲間をお待ちしております。
                      </p>
                      <div className={Style.recruitContent__buttonBox}>
                        <LinkButton className={Style.recruitContent__button} color="black" text="JOIN US" url="/recruit/" />
                      </div>
                    </div>
                    <div className={Style.recruitContent__videoCol}>
                      <video autoPlay muted loop playsInline>
                        <source src="/e_ize_movie_1.mp4" type="video/mp4" />
                      </video>
                    </div>
                  </div>
                </div>

                <div className={`${Style.futsalContact} ${Style.fadeUp}`}>
                  <div className={Style.futsalContact__inner}>
                    <div className={Style.futsalContact__imgCol}>
                      <Image filename="futsal.png" alt="社内フットサルイベント" />
                    </div>
                    <div className={Style.futsalContact__txtCol}>
                      <p className={Style.futsalContact__txt}>
                        体を動かしたい社員が集まる、自由参加のフットサル。
                        普段はデスクに向かうエンジニアも、オフの時間はチームで汗を流しています。
                        部署や役職に関係なくフラットに交流できる社内イベントのひとつとして、継続的に活動を行っています。
                      </p>
                      <div className={Style.futsalContact__buttonBox}>
                        <LinkButton className={Style.futsalContact__button} color="black" text="CONTACT US" url="/contact/" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </Layout>
    </>
  );
};

export default Index;
